import { ArrowRight, Home, Upload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const DirectoryPage = ({
  sharedFormData,
  updateSharedFormData,
  isEditMode = false,
}) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [batchPricing, setBatchPricing] = useState({});

  // Search state (only for create mode)
  const [searchMobile, setSearchMobile] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [existingDirectory, setExistingDirectory] = useState(null);
  const [showForm, setShowForm] = useState(isEditMode);
  const [isNewEntry, setIsNewEntry] = useState(isEditMode ? false : true);
  // New state to track if we're in edit mode from search
  const [isEditModeFromSearch, setIsEditModeFromSearch] = useState(false);
  // Store the ID of the found directory for updating
  const [foundDirectoryId, setFoundDirectoryId] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: sharedFormData?.name || "",
    batch: sharedFormData?.batch || "",
    mobile: sharedFormData?.mobile || "",
    email: sharedFormData?.email || "",
    profession: sharedFormData?.profession || "",
    officeAddress: sharedFormData?.officeAddress || "",
    permanentAddress: sharedFormData?.permanentAddress || "",
    professionalDetails: sharedFormData?.professionalDetails || "",
    bloodGroup: sharedFormData?.bloodGroup || "",
    imageUrl: sharedFormData?.imageUrl || "",
  });

  // Store the original mobile from search to prevent it from being cleared
  const [originalMobile, setOriginalMobile] = useState("");

  const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
  const navigate = useNavigate();
  const { user } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;

  // Determine if we're in edit mode (either from prop or from search)
  const effectiveEditMode = isEditMode || isEditModeFromSearch;

  // Fetch directory data for edit mode (from URL)
  useEffect(() => {
    if (isEditMode && id) {
      const fetchDirectoryData = async () => {
        try {
          setIsFetching(true);
          const data = await api.directory.getById(id);

          setFormData({
            name: data.name || "",
            batch: data.batch || "",
            mobile: data.mobile || "",
            email: data.email || "",
            profession: data.profession || "",
            officeAddress: data.officeAddress || "",
            permanentAddress: data.permanentAddress || "",
            professionalDetails: data.professionalDetails || "",
            bloodGroup: data.bloodGroup || "",
            imageUrl: data.imageUrl || "",
          });

          setOriginalMobile(data.mobile || "");

          if (data.imageUrl) {
            setImagePreview(`${API_BASE_URL}${data.imageUrl}`);
          }
        } catch (error) {
          console.error("Error fetching directory data:", error);
          alert("Failed to load directory data");
          navigate("/directory");
        } finally {
          setIsFetching(false);
        }
      };

      fetchDirectoryData();
    }
  }, [isEditMode, id, navigate, API_BASE_URL]);

  // Update local state when sharedFormData changes (for create mode)
  useEffect(() => {
    if (!isEditMode && !isEditModeFromSearch && sharedFormData) {
      setFormData({
        name: sharedFormData.name || "",
        batch: sharedFormData.batch || "",
        mobile: sharedFormData.mobile || "",
        email: sharedFormData.email || "",
        profession: sharedFormData.profession || "",
        officeAddress: sharedFormData.officeAddress || "",
        permanentAddress: sharedFormData.permanentAddress || "",
        professionalDetails: sharedFormData.professionalDetails || "",
        bloodGroup: sharedFormData.bloodGroup || "",
        imageUrl: sharedFormData.imageUrl || "",
      });
    }
  }, [sharedFormData, isEditMode, isEditModeFromSearch]);

  // Fetch batch pricing
  useEffect(() => {
    const fetchBatchPricing = async () => {
      try {
        const data = await api.batchPricing.getAll();
        const pricingMap = {};
        data.forEach((bp) => {
          pricingMap[bp.batchName] = {
            participantFee: bp.participantFee,
            adultFee: bp.adultFee,
            above10Fee: bp.above10Fee,
            below10Fee: bp.below10Fee,
          };
        });
        setBatchPricing(pricingMap);
      } catch (err) {
        console.error("Error fetching batch pricing:", err);
      }
    };

    fetchBatchPricing();
  }, []);

  // Search for existing directory by mobile (only for create mode)
  const handleSearchDirectory = async () => {
    if (!searchMobile || searchMobile.length < 11) {
      setSearchError("Please enter a valid 11-digit mobile number");
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError("");
      setExistingDirectory(null);
      setIsNewEntry(false);
      setIsEditModeFromSearch(false);
      setFoundDirectoryId(null);

      const response = await api.directory.searchByMobile(searchMobile);

      if (response && response.length > 0) {
        // Found existing directory - SET EDIT MODE TO TRUE
        const directory = response[0];
        setExistingDirectory(directory);
        setIsEditModeFromSearch(true); // Set edit mode to true
        setFoundDirectoryId(directory.id); // Store the ID for updating

        // Auto-fill the form with existing data (editable)
        setFormData({
          name: directory.name || "",
          batch: directory.batch || "",
          mobile: directory.mobile || searchMobile,
          email: directory.email || "",
          profession: directory.profession || "",
          officeAddress: directory.officeAddress || "",
          permanentAddress: directory.permanentAddress || "",
          professionalDetails: directory.professionalDetails || "",
          bloodGroup: directory.bloodGroup || "",
          imageUrl: directory.imageUrl || "",
        });

        setOriginalMobile(directory.mobile || searchMobile);

        if (directory.imageUrl) {
          setImagePreview(`${API_BASE_URL}${directory.imageUrl}`);
        }

        setShowForm(true);
        setIsNewEntry(false);
      } else {
        // No existing directory found - ALLOW CREATE NEW (not edit mode)
        setFormData({
          name: "",
          batch: "",
          mobile: searchMobile,
          email: "",
          profession: "",
          officeAddress: "",
          permanentAddress: "",
          professionalDetails: "",
          bloodGroup: "",
          imageUrl: "",
        });

        setOriginalMobile(searchMobile);
        setImagePreview(null);
        setShowForm(true);
        setIsNewEntry(true);
        setIsEditModeFromSearch(false);
        setFoundDirectoryId(null);
      }
    } catch (error) {
      console.error("Error searching directory:", error);
      setSearchError("Failed to search directory. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResetSearch = () => {
    setSearchMobile("");
    setExistingDirectory(null);
    setShowForm(false);
    setIsNewEntry(false);
    setIsEditModeFromSearch(false);
    setFoundDirectoryId(null);
    setFormData({
      name: "",
      batch: "",
      mobile: "",
      email: "",
      profession: "",
      officeAddress: "",
      permanentAddress: "",
      professionalDetails: "",
      bloodGroup: "",
      imageUrl: "",
    });
    setOriginalMobile("");
    setImagePreview(null);
    setSearchError("");
  };

  const handleInputChange = (field, value) => {
    // In effective edit mode OR for new entries, allow changes
    if (effectiveEditMode || isNewEntry) {
      // Only prevent mobile field from being changed in create mode for new entries
      if (field === "mobile" && isNewEntry && !effectiveEditMode) {
        return; // Don't allow mobile to be changed for new entries
      }

      setFormData((prev) => ({ ...prev, [field]: value }));

      if (!effectiveEditMode && updateSharedFormData) {
        updateSharedFormData(field, value);
      }

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  // Ensure mobile field is always set to originalMobile in create mode
  useEffect(() => {
    if (originalMobile && !effectiveEditMode) {
      setFormData((prev) => ({
        ...prev,
        mobile: originalMobile,
      }));
    }
  }, [originalMobile, effectiveEditMode]);

  const handleHomeClick = () => {
    navigate("/home");
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    // Mobile validation
    const mobileToValidate = effectiveEditMode ? formData.mobile : originalMobile || formData.mobile;
    if (!mobileToValidate || !mobileToValidate.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d{11}$/.test(mobileToValidate)) {
      newErrors.mobile = "Mobile must be 11 digits";
    }

    // Email validation - optional
    if (
      formData.email &&
      formData.email.trim() &&
      !/\S+@\S+\.\S+/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      alert("Please upload a valid image file (JPEG, PNG, GIF, WebP)");
      return;
    }

    if (file.size > maxSize) {
      alert("Image size should be less than 10MB");
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_BASE_URL}/api/upload/image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();

      handleInputChange("imageUrl", data.url);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Image upload error:", error);
      alert("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    handleInputChange("imageUrl", "");
    setImagePreview(null);
  };

  const handleSubmit = async () => {
    console.log("Form data before submission:", formData);
    console.log("Original mobile:", originalMobile);
    console.log("Is edit mode from search:", isEditModeFromSearch);
    console.log("Found directory ID:", foundDirectoryId);

    // Use appropriate mobile value based on mode
    const mobileToUse = effectiveEditMode ? formData.mobile : originalMobile || formData.mobile;

    if (!validateForm()) {
      console.log("Validation failed with errors:", errors);
      return;
    }

    // Double-check required fields
    if (!formData.name || !formData.name.trim()) {
      alert("Please enter a name");
      return;
    }

    if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
      alert("Please ensure mobile number is 11 digits");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name || "",
        batch: formData.batch || "",
        mobile: mobileToUse || "",
        email: formData.email || "",
        profession: formData.profession || "",
        officeAddress: formData.officeAddress || "",
        permanentAddress: formData.permanentAddress || "",
        professionalDetails: formData.professionalDetails || "",
        bloodGroup: formData.bloodGroup || "",
        imageUrl: formData.imageUrl || "",
        userId: user?.id || null,
      };

      console.log("Saving payload:", payload);

      // Check if we're in edit mode (either from URL or from search)
      if (isEditMode && id) {
        // Update existing directory (from URL edit mode)
        await api.directory.updateById(id, payload);
        alert("Directory information updated successfully ✅");
        
        // Navigate back or refresh
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else if (isEditModeFromSearch && foundDirectoryId) {
        // Update existing directory (from search result)
        await api.directory.updateById(foundDirectoryId, payload);
        alert("Directory information updated successfully ✅");
        
        // Reset to search mode
        setTimeout(() => {
          handleResetSearch();
        }, 1500);
      } else {
        // Create new directory
        await api.directory.create(payload);
        alert("Directory information saved successfully ✅");

        // Reset form for create mode
        handleResetSearch();
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Save error:", error);
      alert(`Failed to ${(isEditMode || isEditModeFromSearch) ? 'update' : 'save'} directory: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="form-container px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-4"></div>
          <p>Loading directory data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container px-4">
      <h2 className="form-title">
        {effectiveEditMode ? "Edit Directory Entry" : "Directory"}
      </h2>
      <h5
        style={{ textAlign: "center", color: "#58db8c", marginBottom: "20PX" }}
      >
        KGC'97 Day 2026
      </h5>

      {showSuccess && (
        <div className="success-message">🎉 Directory {effectiveEditMode ? 'updated' : 'saved'} successfully!</div>
      )}

      {/* Mobile Search Section (only for create mode and not in edit mode from search) */}
      {!isEditMode && !isEditModeFromSearch && !showForm && (
        <div className="search-section mb-6">
          <div className="text-center mb-4">
            <p className="text-gray-300">Enter your 11 digits mobile number</p>
          </div>
          <div className="row justify-content-center">
            <div className="col-12 col-md-8 col-lg-6">
              <div className="input-group input-group-lg">
                <input
                  type="text"
                  className="form-control bg-light text-dark border-green-500"
                  placeholder="01XXXXXXXX"
                  value={searchMobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 11) {
                      setSearchMobile(value);
                      setSearchError("");
                    }
                  }}
                  maxLength="11"
                  style={{ height: "50px" }}
                />
                <button
                  className="btn btn-search d-flex align-items-center justify-content-center"
                  onClick={handleSearchDirectory}
                  disabled={searchLoading || !searchMobile.trim()}
                  style={{
                    height: "50px",
                    width: "60px",
                    padding: "0 15px",
                  }}
                  title="Search"
                >
                  {searchLoading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <ArrowRight size={24} />
                  )}
                </button>
              </div>
              {searchError && (
                <div className="text-danger small mt-2 text-center">
                  {searchError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show form if in edit mode OR after search */}
      {(effectiveEditMode || showForm) && (
        <form onSubmit={(e) => e.preventDefault()}>
          {/* Existing Directory Notification - UPDATED to show edit mode */}
          {!isEditMode && isEditModeFromSearch && existingDirectory && (
            <div className="alert alert-primary mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>✏️ Edit Existing Directory Entry</strong>
                  <div className="small">
                    Directory entry found for mobile: {originalMobile}. You can edit all fields below.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* New Entry Notification (only for create mode) */}
          {!isEditMode && !isEditModeFromSearch && isNewEntry && (
            <div className="alert alert-warning mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>➕ Create New Directory Entry</strong>
                  <div className="small">
                    No existing directory found for mobile: {originalMobile}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Mode Notification (from URL) */}
          {isEditMode && (
            <div className="alert alert-primary mb-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>✏️ Edit Directory Entry</strong>
                  <div className="small">
                    You can edit all fields including the image.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Field */}
          <div className="form-group">
            <label className="form-label">মোবাইল নম্বর*</label>
            {effectiveEditMode ? (
              // In edit mode (either from URL or from search), allow mobile to be edited
              <input
                type="text"
                className="form-input"
                placeholder="01XXXXXXXXX"
                value={formData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 11) {
                    handleInputChange("mobile", value);
                  }
                }}
                disabled={loading || uploadingImage}
                maxLength="11"
              />
            ) : (
              // In create mode for new entries, show as read-only display
              <div className="mobile-display">
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#e9ecef",
                    color: "#495057",
                    borderRadius: "5px",
                    border: "1px solid #dee2e6",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  {originalMobile || formData.mobile || "No mobile number"}
                </div>
                <input
                  type="hidden"
                  name="mobile"
                  value={originalMobile || formData.mobile || ""}
                />
              </div>
            )}
            {errors.mobile && (
              <div className="error-message">{errors.mobile}</div>
            )}
          </div>

          {/* Name */}
          <div className="form-group">
            <label className="form-label">নাম *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              disabled={loading || uploadingImage}
            />
            {errors.name && <div className="error-message">{errors.name}</div>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label">ইমেইল </label>
            <input
              type="email"
              className="form-input"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled={loading || uploadingImage}
            />
            {errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>

          {/* Batch */}
          <div style={{ width: "100%", position: "relative" }}>
            <label className="form-label">বিভাগ </label>
            <select
              value={formData.batch}
              onChange={(e) => handleInputChange("batch", e.target.value)}
              disabled={loading || uploadingImage}
              style={{
                width: "100%",
                padding: "8px 15px",
                borderRadius: "0px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background: "rgba(255,255,255,0.1)",
                color: "white",
                fontSize: "1rem",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                backgroundImage: `url("data:image/svg+xml,%3Csvg fill='white' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "1rem",
                marginBottom: "15px",
              }}
            >
              <option
                value=""
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                Select your group
              </option>
              {Object.keys(batchPricing).map((batch) => (
                <option
                  key={batch}
                  value={batch}
                  style={{ backgroundColor: "#cffae0", color: "black" }}
                >
                  {batch}
                </option>
              ))}
            </select>
            {errors.batch && (
              <div className="error-message">{errors.batch}</div>
            )}
          </div>

          {/* Profession */}
          <div className="form-group">
            <label className="form-label">পেশা </label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter your profession"
              value={formData.profession}
              onChange={(e) => handleInputChange("profession", e.target.value)}
              disabled={loading || uploadingImage}
            />
            {errors.profession && (
              <div className="error-message">{errors.profession}</div>
            )}
          </div>

          {/* Professional Details */}
          <div className="form-group">
            <label className="form-label">পদবি, অফিসের নাম </label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter designation & office details"
              value={formData.professionalDetails}
              onChange={(e) =>
                handleInputChange("professionalDetails", e.target.value)
              }
              disabled={loading || uploadingImage}
            />
            {errors.professionalDetails && (
              <div className="error-message">{errors.professionalDetails}</div>
            )}
          </div>

          {/* Office Address */}
          <div className="form-group">
            <label className="form-label">বর্তমান ঠিকনা</label>
            <textarea
              className="form-input"
              placeholder="Enter your present address"
              value={formData.officeAddress}
              onChange={(e) =>
                handleInputChange("officeAddress", e.target.value)
              }
              disabled={loading || uploadingImage}
              rows={2}
              style={{ resize: "vertical" }}
            />
            {errors.officeAddress && (
              <div className="error-message">{errors.officeAddress}</div>
            )}
          </div>

          {/* Permanent Address */}
          <div className="form-group">
            <label className="form-label">স্থায়ী ঠিকনা</label>
            <textarea
              className="form-input"
              placeholder="Enter your permanent address"
              value={formData.permanentAddress}
              onChange={(e) =>
                handleInputChange("permanentAddress", e.target.value)
              }
              disabled={loading || uploadingImage}
              rows={2}
              style={{ resize: "vertical" }}
            />
            {errors.permanentAddress && (
              <div className="error-message">{errors.permanentAddress}</div>
            )}
          </div>

          {/* Blood Group */}
          <div className="form-group">
            <label className="form-label">রক্তের গ্রুপ </label>
            <select
              value={formData.bloodGroup}
              onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
              disabled={loading || uploadingImage}
              style={{
                width: "100%",
                padding: "8px 15px",
                borderRadius: "0px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                background: "rgba(255,255,255,0.1)",
                color: "white",
                fontSize: "1rem",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backdropFilter: "blur(8px)",
                transition: "all 0.3s ease",
                backgroundImage: `url("data:image/svg+xml,%3Csvg fill='white' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "1rem",
                marginBottom: "15px",
              }}
            >
              <option
                value=""
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                Select your blood group
              </option>
              {bloodGroups.map((bloodGroup) => (
                <option
                  key={bloodGroup}
                  value={bloodGroup}
                  style={{ backgroundColor: "#cffae0", color: "black" }}
                >
                  {bloodGroup}
                </option>
              ))}
            </select>
            {errors.bloodGroup && (
              <div className="error-message">{errors.bloodGroup}</div>
            )}
          </div>

          {/* Image Upload Section (available for all modes when editable) */}
          {/* {(!isNewEntry || effectiveEditMode) && ( */}
            <div className="form-group">
              <label className="form-label">ছবি আপলোড</label>

              {imagePreview || formData.imageUrl ? (
                <div className="relative mb-3">
                  <div className="relative w-32 h-32 mx-auto">
                    <img
                      src={
                        imagePreview || `${API_BASE_URL}${formData.imageUrl}`
                      }
                      alt="Preview"
                      className="w-full h-full object-cover rounded-lg border-2 border-white/20"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                      disabled={uploadingImage}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="text-center mt-2">
                    <label
                      htmlFor="image-upload"
                      className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer"
                    >
                      Change Image
                    </label>
                  </div>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-white/40 transition"
                  onClick={() =>
                    document.getElementById("image-upload").click()
                  }
                >
                  <Upload className="mx-auto mb-2 text-white" size={24} />
                  <p className="text-sm text-white">
                    {effectiveEditMode ? "Change Image" : "ছবি আপলোড করুন"}
                  </p>
                  <p className="text-xs text-white/60 mt-1">
                    JPEG, PNG, GIF, WebP (Max 10MB)
                  </p>
                </div>
              )}

              <input
                id="image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleImageUpload(e.target.files[0]);
                  }
                }}
                disabled={uploadingImage || loading}
              />

              {uploadingImage && (
                <div className="text-center mt-2">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                  <p className="text-sm mt-1">আপলোড হচ্ছে...</p>
                </div>
              )}
            </div>
          {/* )} */}

          {/* Buttons */}
          <div className="button-group flex gap-4">
            {/* Submit/Update Button */}
            <button
              type="button"
              className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#1cbd2f" }}
              onClick={handleSubmit}
              disabled={loading || uploadingImage}
            >
              {loading 
                ? (effectiveEditMode ? "Updating..." : "Saving...") 
                : (effectiveEditMode ? "Update" : "Submit")}
            </button>

            {/* New Search Button (only in create/search mode, not in edit mode) */}
            {!isEditMode && !isEditModeFromSearch && (
              <button
                type="button"
                className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
                onClick={handleResetSearch}
                disabled={loading || uploadingImage}
              >
                New Search
              </button>
            )}

            {/* Cancel Edit Button (in edit mode from search) */}
            {isEditModeFromSearch && (
              <button
                type="button"
                className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
                onClick={handleResetSearch}
                disabled={loading || uploadingImage}
              >
                Cancel
              </button>
            )}

            {/* Cancel Edit Button (only in URL edit mode) */}
            {isEditMode && (
              <button
                type="button"
                className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
                onClick={() => navigate(-1)}
                disabled={loading || uploadingImage}
              >
                Cancel
              </button>
            )}

            {/* Home Button */}
            <button
              type="button"
              className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
              onClick={handleHomeClick}
              disabled={loading || uploadingImage}
            >
              <Home size={18} />
              Go To Home
            </button>
          </div>
        </form>
      )}

      {/* Show only home button if not showing form and not in edit mode */}
      {!isEditMode && !isEditModeFromSearch && !showForm && (
        <div className="flex justify-center text-center mt-4">
          <button
            type="button"
            className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
            onClick={handleHomeClick}
          >
            <Home size={18} />
            Go To Home
          </button>
        </div>
      )}
    </div>
  );
};

export default DirectoryPage;

// import { ArrowRight, Home, Upload, X } from "lucide-react";
// import { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const DirectoryPage = ({
//   sharedFormData,
//   updateSharedFormData,
//   isEditMode = false,
// }) => {
//   const { id } = useParams();
//   const [loading, setLoading] = useState(false);
//   const [isFetching, setIsFetching] = useState(isEditMode);
//   const [uploadingImage, setUploadingImage] = useState(false);
//   const [imagePreview, setImagePreview] = useState(null);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [batchPricing, setBatchPricing] = useState({});

//   // Search state (only for create mode)
//   const [searchMobile, setSearchMobile] = useState("");
//   const [searchLoading, setSearchLoading] = useState(false);
//   const [searchError, setSearchError] = useState("");
//   const [existingDirectory, setExistingDirectory] = useState(null);
//   const [showForm, setShowForm] = useState(isEditMode);
//   const [isNewEntry, setIsNewEntry] = useState(isEditMode ? false : true); // In edit mode, it's not a new entry

//   // Form data state
//   const [formData, setFormData] = useState({
//     name: sharedFormData?.name || "",
//     batch: sharedFormData?.batch || "",
//     mobile: sharedFormData?.mobile || "",
//     email: sharedFormData?.email || "",
//     profession: sharedFormData?.profession || "",
//     officeAddress: sharedFormData?.officeAddress || "",
//     permanentAddress: sharedFormData?.permanentAddress || "",
//     professionalDetails: sharedFormData?.professionalDetails || "",
//     bloodGroup: sharedFormData?.bloodGroup || "",
//     imageUrl: sharedFormData?.imageUrl || "",
//   });

//   // Store the original mobile from search to prevent it from being cleared
//   const [originalMobile, setOriginalMobile] = useState("");

//   const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
//   const navigate = useNavigate();
//   const { user } = useAuth();
//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;

//   // Fetch directory data for edit mode
//   useEffect(() => {
//     if (isEditMode && id) {
//       const fetchDirectoryData = async () => {
//         try {
//           setIsFetching(true);
//           const data = await api.directory.getById(id);

//           setFormData({
//             name: data.name || "",
//             batch: data.batch || "",
//             mobile: data.mobile || "",
//             email: data.email || "",
//             profession: data.profession || "",
//             officeAddress: data.officeAddress || "",
//             permanentAddress: data.permanentAddress || "",
//             professionalDetails: data.professionalDetails || "",
//             bloodGroup: data.bloodGroup || "",
//             imageUrl: data.imageUrl || "",
//           });

//           setOriginalMobile(data.mobile || "");

//           if (data.imageUrl) {
//             setImagePreview(`${API_BASE_URL}${data.imageUrl}`);
//           }
//         } catch (error) {
//           console.error("Error fetching directory data:", error);
//           alert("Failed to load directory data");
//           navigate("/directory");
//         } finally {
//           setIsFetching(false);
//         }
//       };

//       fetchDirectoryData();
//     }
//   }, [isEditMode, id, navigate, API_BASE_URL]);

//   // Update local state when sharedFormData changes (for create mode)
//   useEffect(() => {
//     if (!isEditMode && sharedFormData) {
//       setFormData({
//         name: sharedFormData.name || "",
//         batch: sharedFormData.batch || "",
//         mobile: sharedFormData.mobile || "",
//         email: sharedFormData.email || "",
//         profession: sharedFormData.profession || "",
//         officeAddress: sharedFormData.officeAddress || "",
//         permanentAddress: sharedFormData.permanentAddress || "",
//         professionalDetails: sharedFormData.professionalDetails || "",
//         bloodGroup: sharedFormData.bloodGroup || "",
//         imageUrl: sharedFormData.imageUrl || "",
//       });
//     }
//   }, [sharedFormData, isEditMode]);

//   // Fetch batch pricing
//   useEffect(() => {
//     const fetchBatchPricing = async () => {
//       try {
//         const data = await api.batchPricing.getAll();
//         const pricingMap = {};
//         data.forEach((bp) => {
//           pricingMap[bp.batchName] = {
//             participantFee: bp.participantFee,
//             adultFee: bp.adultFee,
//             above10Fee: bp.above10Fee,
//             below10Fee: bp.below10Fee,
//           };
//         });
//         setBatchPricing(pricingMap);
//       } catch (err) {
//         console.error("Error fetching batch pricing:", err);
//       }
//     };

//     fetchBatchPricing();
//   }, []);

//   // Search for existing directory by mobile (only for create mode)
//   const handleSearchDirectory = async () => {
//     if (!searchMobile || searchMobile.length < 11) {
//       setSearchError("Please enter a valid 11-digit mobile number");
//       return;
//     }

//     try {
//       setSearchLoading(true);
//       setSearchError("");
//       setExistingDirectory(null);
//       setIsNewEntry(false);

//       const response = await api.directory.searchByMobile(searchMobile);

//       if (response && response.length > 0) {
//         // Found existing directory - SHOW VIEW ONLY
//         const directory = response[0];
//         setExistingDirectory(directory);

//         // Auto-fill the form with existing data (read-only)
//         setFormData({
//           name: directory.name || "",
//           batch: directory.batch || "",
//           mobile: directory.mobile || searchMobile,
//           email: directory.email || "",
//           profession: directory.profession || "",
//           officeAddress: directory.officeAddress || "",
//           permanentAddress: directory.permanentAddress || "",
//           professionalDetails: directory.professionalDetails || "",
//           bloodGroup: directory.bloodGroup || "",
//           imageUrl: directory.imageUrl || "",
//         });

//         setOriginalMobile(directory.mobile || searchMobile);

//         if (directory.imageUrl) {
//           setImagePreview(`${API_BASE_URL}${directory.imageUrl}`);
//         }

//         setShowForm(true);
//         setIsNewEntry(false);
//       } else {
//         // No existing directory found - ALLOW CREATE NEW
//         setFormData({
//           name: "",
//           batch: "",
//           mobile: searchMobile,
//           email: "",
//           profession: "",
//           officeAddress: "",
//           permanentAddress: "",
//           professionalDetails: "",
//           bloodGroup: "",
//           imageUrl: "",
//         });

//         setOriginalMobile(searchMobile);
//         setImagePreview(null);
//         setShowForm(true);
//         setIsNewEntry(true);
//       }
//     } catch (error) {
//       console.error("Error searching directory:", error);
//       setSearchError("Failed to search directory. Please try again.");
//     } finally {
//       setSearchLoading(false);
//     }
//   };

//   const handleResetSearch = () => {
//     setSearchMobile("");
//     setExistingDirectory(null);
//     setShowForm(false);
//     setIsNewEntry(false);
//     setFormData({
//       name: "",
//       batch: "",
//       mobile: "",
//       email: "",
//       profession: "",
//       officeAddress: "",
//       permanentAddress: "",
//       professionalDetails: "",
//       bloodGroup: "",
//       imageUrl: "",
//     });
//     setOriginalMobile("");
//     setImagePreview(null);
//     setSearchError("");
//   };

//   const handleInputChange = (field, value) => {
//     // In edit mode, allow all fields to be changed
//     if (isEditMode || isNewEntry) {
//       // Only prevent mobile field from being changed in create mode
//       if (field === "mobile" && isNewEntry) {
//         return; // Don't allow mobile to be changed in create mode
//       }

//       setFormData((prev) => ({ ...prev, [field]: value }));

//       if (!isEditMode && updateSharedFormData) {
//         updateSharedFormData(field, value);
//       }

//       if (errors[field]) {
//         setErrors((prev) => ({ ...prev, [field]: "" }));
//       }
//     }
//   };

//   // Ensure mobile field is always set to originalMobile in create mode
//   useEffect(() => {
//     if (originalMobile && !isEditMode) {
//       setFormData((prev) => ({
//         ...prev,
//         mobile: originalMobile,
//       }));
//     }
//   }, [originalMobile, isEditMode]);

//   const handleHomeClick = () => {
//     navigate("/home");
//   };

//   const validateForm = () => {
//     const newErrors = {};

//     // Name validation
//     if (!formData.name || !formData.name.trim()) {
//       newErrors.name = "Name is required";
//     }

//     // Mobile validation
//     const mobileToValidate = isEditMode ? formData.mobile : originalMobile || formData.mobile;
//     if (!mobileToValidate || !mobileToValidate.trim()) {
//       newErrors.mobile = "Mobile number is required";
//     } else if (!/^\d{11}$/.test(mobileToValidate)) {
//       newErrors.mobile = "Mobile must be 11 digits";
//     }

//     // Email validation - optional
//     if (
//       formData.email &&
//       formData.email.trim() &&
//       !/\S+@\S+\.\S+/.test(formData.email)
//     ) {
//       newErrors.email = "Please enter a valid email address";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleImageUpload = async (file) => {
//     if (!file) return;

//     const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
//     const maxSize = 10 * 1024 * 1024; // 10MB

//     if (!validTypes.includes(file.type)) {
//       alert("Please upload a valid image file (JPEG, PNG, GIF, WebP)");
//       return;
//     }

//     if (file.size > maxSize) {
//       alert("Image size should be less than 10MB");
//       return;
//     }

//     setUploadingImage(true);

//     try {
//       const formData = new FormData();
//       formData.append("file", file);

//       const response = await fetch(`${API_BASE_URL}/api/upload/image`, {
//         method: "POST",
//         body: formData,
//       });

//       if (!response.ok) throw new Error("Upload failed");

//       const data = await response.json();

//       handleInputChange("imageUrl", data.url);

//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setImagePreview(reader.result);
//       };
//       reader.readAsDataURL(file);
//     } catch (error) {
//       console.error("Image upload error:", error);
//       alert("Failed to upload image");
//     } finally {
//       setUploadingImage(false);
//     }
//   };

//   const handleRemoveImage = () => {
//     handleInputChange("imageUrl", "");
//     setImagePreview(null);
//   };

//   const handleSubmit = async () => {
//     console.log("Form data before submission:", formData);
//     console.log("Original mobile:", originalMobile);

//     // Use appropriate mobile value based on mode
//     const mobileToUse = isEditMode ? formData.mobile : originalMobile || formData.mobile;

//     if (!validateForm()) {
//       console.log("Validation failed with errors:", errors);
//       return;
//     }

//     // Double-check required fields
//     if (!formData.name || !formData.name.trim()) {
//       alert("Please enter a name");
//       return;
//     }

//     if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
//       alert("Please ensure mobile number is 11 digits");
//       return;
//     }

//     setLoading(true);
//     try {
//       const payload = {
//         name: formData.name || "",
//         batch: formData.batch || "",
//         mobile: mobileToUse || "",
//         email: formData.email || "",
//         profession: formData.profession || "",
//         officeAddress: formData.officeAddress || "",
//         permanentAddress: formData.permanentAddress || "",
//         professionalDetails: formData.professionalDetails || "",
//         bloodGroup: formData.bloodGroup || "",
//         imageUrl: formData.imageUrl || "",
//         userId: user?.id || null,
//       };

//       console.log("Saving payload:", payload);

//       if (isEditMode && id) {
//         // Update existing directory
//         await api.directory.updateById(id, payload);
//         alert("Directory information updated successfully ✅");
        
//         // Navigate back or refresh
//         setTimeout(() => {
//           navigate(-1);
//         }, 1500);
//       } else {
//         // Create new directory
//         await api.directory.create(payload);
//         alert("Directory information saved successfully ✅");

//         // Reset form for create mode
//         handleResetSearch();
//       }

//       setShowSuccess(true);
//       setTimeout(() => setShowSuccess(false), 3000);
//     } catch (error) {
//       console.error("Save error:", error);
//       alert(`Failed to ${isEditMode ? 'update' : 'save'} directory: ${error.message}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (isFetching) {
//     return (
//       <div className="form-container px-4 flex items-center justify-center">
//         <div className="text-center">
//           <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-4"></div>
//           <p>Loading directory data...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="form-container px-4">
//       <h2 className="form-title">
//         {isEditMode ? "Edit Directory Entry" : "Directory"}
//       </h2>
//       <h5
//         style={{ textAlign: "center", color: "#58db8c", marginBottom: "20PX" }}
//       >
//         KGC'97 Day 2026
//       </h5>

//       {showSuccess && (
//         <div className="success-message">🎉 Directory {isEditMode ? 'updated' : 'saved'} successfully!</div>
//       )}

//       {/* Mobile Search Section (only for create mode) */}
//       {!isEditMode && !showForm && (
//         <div className="search-section mb-6">
//           <div className="text-center mb-4">
//             <p className="text-gray-300">Enter your 11 digits mobile number</p>
//           </div>
//           <div className="row justify-content-center">
//             <div className="col-12 col-md-8 col-lg-6">
//               <div className="input-group input-group-lg">
//                 <input
//                   type="text"
//                   className="form-control bg-light text-dark border-green-500"
//                   placeholder="01XXXXXXXX"
//                   value={searchMobile}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, "");
//                     if (value.length <= 11) {
//                       setSearchMobile(value);
//                       setSearchError("");
//                     }
//                   }}
//                   maxLength="11"
//                   style={{ height: "50px" }}
//                 />
//                 <button
//                   className="btn btn-search d-flex align-items-center justify-content-center"
//                   onClick={handleSearchDirectory}
//                   disabled={searchLoading || !searchMobile.trim()}
//                   style={{
//                     height: "50px",
//                     width: "60px",
//                     padding: "0 15px",
//                   }}
//                   title="Search"
//                 >
//                   {searchLoading ? (
//                     <span className="spinner-border spinner-border-sm" />
//                   ) : (
//                     <ArrowRight size={24} />
//                   )}
//                 </button>
//               </div>
//               {searchError && (
//                 <div className="text-danger small mt-2 text-center">
//                   {searchError}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Show form if in edit mode OR after search */}
//       {(isEditMode || showForm) && (
//         <form onSubmit={(e) => e.preventDefault()}>
//           {/* Existing Directory Notification (only for create mode) */}
//           {!isEditMode && existingDirectory && !isNewEntry && (
//             <div className="alert alert-info mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>✓ Directory Entry Found</strong>
//                   <div className="small">
//                     This directory entry already exists. You can view the
//                     details below.
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* New Entry Notification (only for create mode) */}
//           {!isEditMode && isNewEntry && (
//             <div className="alert alert-warning mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>➕ Create New Directory Entry</strong>
//                   <div className="small">
//                     No existing directory found for mobile: {originalMobile}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Edit Mode Notification */}
//           {isEditMode && (
//             <div className="alert alert-primary mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>✏️ Edit Directory Entry</strong>
//                   <div className="small">
//                     You can edit all fields including the image.
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Mobile Field */}
//           <div className="form-group">
//             <label className="form-label">মোবাইল নম্বর*</label>
//             {isEditMode ? (
//               // In edit mode, allow mobile to be edited
//               <input
//                 type="text"
//                 className="form-input"
//                 placeholder="01XXXXXXXXX"
//                 value={formData.mobile}
//                 onChange={(e) => {
//                   const value = e.target.value.replace(/\D/g, "");
//                   if (value.length <= 11) {
//                     handleInputChange("mobile", value);
//                   }
//                 }}
//                 disabled={loading || uploadingImage}
//                 maxLength="11"
//               />
//             ) : (
//               // In create mode, show as read-only display
//               <div className="mobile-display">
//                 <div
//                   style={{
//                     padding: "12px",
//                     backgroundColor: "#e9ecef",
//                     color: "#495057",
//                     borderRadius: "5px",
//                     border: "1px solid #dee2e6",
//                     fontSize: "16px",
//                     fontWeight: "bold",
//                   }}
//                 >
//                   {originalMobile || formData.mobile || "No mobile number"}
//                 </div>
//                 <input
//                   type="hidden"
//                   name="mobile"
//                   value={originalMobile || formData.mobile || ""}
//                 />
//               </div>
//             )}
//             {errors.mobile && (
//               <div className="error-message">{errors.mobile}</div>
//             )}
//           </div>

//           {/* Name */}
//           <div className="form-group">
//             <label className="form-label">নাম *</label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your full name"
//               value={formData.name}
//               onChange={(e) => handleInputChange("name", e.target.value)}
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               readOnly={!isNewEntry && !isEditMode}
//               style={
//                 (!isNewEntry && !isEditMode)
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.name && <div className="error-message">{errors.name}</div>}
//           </div>

//           {/* Email */}
//           <div className="form-group">
//             <label className="form-label">ইমেইল </label>
//             <input
//               type="email"
//               className="form-input"
//               placeholder="your.email@example.com"
//               value={formData.email}
//               onChange={(e) => handleInputChange("email", e.target.value)}
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               readOnly={!isNewEntry && !isEditMode}
//               style={
//                 (!isNewEntry && !isEditMode)
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.email && (
//               <div className="error-message">{errors.email}</div>
//             )}
//           </div>

//           {/* Batch */}
//           <div style={{ width: "100%", position: "relative" }}>
//             <label className="form-label">বিভাগ </label>
//             <select
//               value={formData.batch}
//               onChange={(e) => handleInputChange("batch", e.target.value)}
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background: (!isNewEntry && !isEditMode) ? "#e9ecef" : "rgba(255,255,255,0.1)",
//                 color: (!isNewEntry && !isEditMode) ? "#495057" : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   (!isNewEntry && !isEditMode) ? "#495057" : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your group
//               </option>
//               {Object.keys(batchPricing).map((batch) => (
//                 <option
//                   key={batch}
//                   value={batch}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {batch}
//                 </option>
//               ))}
//             </select>
//             {errors.batch && (
//               <div className="error-message">{errors.batch}</div>
//             )}
//           </div>

//           {/* Profession */}
//           <div className="form-group">
//             <label className="form-label">পেশা </label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your profession"
//               value={formData.profession}
//               onChange={(e) => handleInputChange("profession", e.target.value)}
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               readOnly={!isNewEntry && !isEditMode}
//               style={
//                 (!isNewEntry && !isEditMode)
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.profession && (
//               <div className="error-message">{errors.profession}</div>
//             )}
//           </div>

//           {/* Professional Details */}
//           <div className="form-group">
//             <label className="form-label">পদবি, অফিসের নাম </label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter designation & office details"
//               value={formData.professionalDetails}
//               onChange={(e) =>
//                 handleInputChange("professionalDetails", e.target.value)
//               }
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               readOnly={!isNewEntry && !isEditMode}
//               style={
//                 (!isNewEntry && !isEditMode)
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.professionalDetails && (
//               <div className="error-message">{errors.professionalDetails}</div>
//             )}
//           </div>

//           {/* Office Address */}
//           <div className="form-group">
//             <label className="form-label">বর্তমান ঠিকনা</label>
//             <textarea
//               className="form-input"
//               placeholder="Enter your present address"
//               value={formData.officeAddress}
//               onChange={(e) =>
//                 handleInputChange("officeAddress", e.target.value)
//               }
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               readOnly={!isNewEntry && !isEditMode}
//               rows={2}
//               style={
//                 (!isNewEntry && !isEditMode)
//                   ? {
//                       backgroundColor: "#e9ecef",
//                       color: "#495057",
//                       resize: "none",
//                     }
//                   : { resize: "vertical" }
//               }
//             />
//             {errors.officeAddress && (
//               <div className="error-message">{errors.officeAddress}</div>
//             )}
//           </div>

//           {/* Permanent Address */}
//           <div className="form-group">
//             <label className="form-label">স্থায়ী ঠিকনা</label>
//             <textarea
//               className="form-input"
//               placeholder="Enter your permanent address"
//               value={formData.permanentAddress}
//               onChange={(e) =>
//                 handleInputChange("permanentAddress", e.target.value)
//               }
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               readOnly={!isNewEntry && !isEditMode}
//               rows={2}
//               style={
//                 (!isNewEntry && !isEditMode)
//                   ? {
//                       backgroundColor: "#e9ecef",
//                       color: "#495057",
//                       resize: "none",
//                     }
//                   : { resize: "vertical" }
//               }
//             />
//             {errors.permanentAddress && (
//               <div className="error-message">{errors.permanentAddress}</div>
//             )}
//           </div>

//           {/* Blood Group */}
//           <div className="form-group">
//             <label className="form-label">রক্তের গ্রুপ </label>
//             <select
//               value={formData.bloodGroup}
//               onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
//               disabled={loading || uploadingImage || (!isNewEntry && !isEditMode)}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background: (!isNewEntry && !isEditMode) ? "#e9ecef" : "rgba(255,255,255,0.1)",
//                 color: (!isNewEntry && !isEditMode) ? "#495057" : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   (!isNewEntry && !isEditMode) ? "#495057" : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your blood group
//               </option>
//               {bloodGroups.map((bloodGroup) => (
//                 <option
//                   key={bloodGroup}
//                   value={bloodGroup}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {bloodGroup}
//                 </option>
//               ))}
//             </select>
//             {errors.bloodGroup && (
//               <div className="error-message">{errors.bloodGroup}</div>
//             )}
//           </div>

//           {/* Image Upload Section (available for both edit and new entry) */}
//           {(isEditMode || isNewEntry) && (
//             <div className="form-group">
//               <label className="form-label">ছবি আপলোড</label>

//               {imagePreview || formData.imageUrl ? (
//                 <div className="relative mb-3">
//                   <div className="relative w-32 h-32 mx-auto">
//                     <img
//                       src={
//                         imagePreview || `${API_BASE_URL}${formData.imageUrl}`
//                       }
//                       alt="Preview"
//                       className="w-full h-full object-cover rounded-lg border-2 border-white/20"
//                     />
//                     <button
//                       type="button"
//                       onClick={handleRemoveImage}
//                       className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
//                       disabled={uploadingImage}
//                     >
//                       <X size={16} />
//                     </button>
//                   </div>
//                   <div className="text-center mt-2">
//                     <label
//                       htmlFor="image-upload"
//                       className="text-sm text-blue-400 hover:text-blue-300 cursor-pointer"
//                     >
//                       Change Image
//                     </label>
//                   </div>
//                 </div>
//               ) : (
//                 <div
//                   className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-white/40 transition"
//                   onClick={() =>
//                     document.getElementById("image-upload").click()
//                   }
//                 >
//                   <Upload className="mx-auto mb-2 text-white" size={24} />
//                   <p className="text-sm text-white">
//                     {isEditMode ? "Change Image" : "ছবি আপলোড করুন"}
//                   </p>
//                   <p className="text-xs text-white/60 mt-1">
//                     JPEG, PNG, GIF, WebP (Max 10MB)
//                   </p>
//                 </div>
//               )}

//               <input
//                 id="image-upload"
//                 type="file"
//                 accept="image/*"
//                 className="hidden"
//                 onChange={(e) => {
//                   if (e.target.files[0]) {
//                     handleImageUpload(e.target.files[0]);
//                   }
//                 }}
//                 disabled={uploadingImage || loading}
//               />

//               {uploadingImage && (
//                 <div className="text-center mt-2">
//                   <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
//                   <p className="text-sm mt-1">আপলোড হচ্ছে...</p>
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Show existing image as read-only (only for existing entries in create mode) */}
//           {!isEditMode && !isNewEntry && formData.imageUrl && (
//             <div className="form-group">
//               <label className="form-label">ছবি</label>
//               <div className="relative mb-3">
//                 <div className="relative w-32 h-32 mx-auto">
//                   <img
//                     src={`${API_BASE_URL}${formData.imageUrl}`}
//                     alt="Directory"
//                     className="w-full h-full object-cover rounded-lg border-2 border-white/20"
//                   />
//                 </div>
//                 <div className="text-center mt-2 text-sm text-gray-400">
//                   Existing image (cannot be modified)
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Buttons */}
//           <div className="button-group flex gap-4">
//             {/* Submit/Update Button */}
//             {(isEditMode || isNewEntry) && (
//               <button
//                 type="button"
//                 className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//                 style={{ backgroundColor: "#1cbd2f" }}
//                 onClick={handleSubmit}
//                 disabled={loading || uploadingImage}
//               >
//                 {loading ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Submit")}
//               </button>
//             )}

//             {/* Cancel/New Search Button */}
//             {/* {!isEditMode && (
//               <button
//                 type="button"
//                 className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//                 onClick={handleResetSearch}
//                 disabled={loading || uploadingImage}
//               >
//                 New Search
//               </button>
//             )} */}

//             {/* Cancel Edit Button (only in edit mode) */}
//             {isEditMode && (
//               <button
//                 type="button"
//                 className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//                 onClick={() => navigate(-1)}
//                 disabled={loading || uploadingImage}
//               >
//                 Cancel
//               </button>
//             )}

//             {/* Home Button */}
//             <button
//               type="button"
//               className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//               onClick={handleHomeClick}
//               disabled={loading || uploadingImage}
//             >
//               <Home size={18} />
//               Go To Home
//             </button>
//           </div>
//         </form>
//       )}

//       {/* Show only home button if not showing form and not in edit mode */}
//       {!isEditMode && !showForm && (
//         <div className="flex justify-center text-center mt-4">
//           <button
//             type="button"
//             className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={handleHomeClick}
//           >
//             <Home size={18} />
//             Go To Home
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default DirectoryPage;






// import { ArrowRight, Home, Upload, X } from "lucide-react";
// import { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const DirectoryPage = ({
//   sharedFormData,
//   updateSharedFormData,
//   isEditMode = false,
// }) => {
//   const { id } = useParams();
//   const [loading, setLoading] = useState(false);
//   const [isFetching, setIsFetching] = useState(isEditMode);
//   const [uploadingImage, setUploadingImage] = useState(false);
//   const [imagePreview, setImagePreview] = useState(null);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [errors, setErrors] = useState({});
//   const [batchPricing, setBatchPricing] = useState({});

//   // Search state
//   const [searchMobile, setSearchMobile] = useState("");
//   const [searchLoading, setSearchLoading] = useState(false);
//   const [searchError, setSearchError] = useState("");
//   const [existingDirectory, setExistingDirectory] = useState(null);
//   const [showForm, setShowForm] = useState(isEditMode);
//   const [isNewEntry, setIsNewEntry] = useState(false);

//   // Form data state
//   const [formData, setFormData] = useState({
//     name: sharedFormData?.name || "",
//     batch: sharedFormData?.batch || "",
//     mobile: sharedFormData?.mobile || "",
//     email: sharedFormData?.email || "",
//     profession: sharedFormData?.profession || "",
//     officeAddress: sharedFormData?.officeAddress || "",
//     permanentAddress: sharedFormData?.permanentAddress || "",
//     professionalDetails: sharedFormData?.professionalDetails || "",
//     bloodGroup: sharedFormData?.bloodGroup || "",
//     imageUrl: sharedFormData?.imageUrl || "",
//   });

//   // Store the original mobile from search to prevent it from being cleared
//   const [originalMobile, setOriginalMobile] = useState("");

//   const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
//   const navigate = useNavigate();
//   const { user } = useAuth();
//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;

//   // Fetch directory data for edit mode
//   useEffect(() => {
//     if (isEditMode && id) {
//       const fetchDirectoryData = async () => {
//         try {
//           setIsFetching(true);
//           const data = await api.directory.getById(id);

//           setFormData({
//             name: data.name || "",
//             batch: data.batch || "",
//             mobile: data.mobile || "",
//             email: data.email || "",
//             profession: data.profession || "",
//             officeAddress: data.officeAddress || "",
//             permanentAddress: data.permanentAddress || "",
//             professionalDetails: data.professionalDetails || "",
//             bloodGroup: data.bloodGroup || "",
//             imageUrl: data.imageUrl || "",
//           });

//           setOriginalMobile(data.mobile || "");

//           if (data.imageUrl) {
//             setImagePreview(`${API_BASE_URL}${data.imageUrl}`);
//           }
//         } catch (error) {
//           console.error("Error fetching directory data:", error);
//           alert("Failed to load directory data");
//           navigate("/directory");
//         } finally {
//           setIsFetching(false);
//         }
//       };

//       fetchDirectoryData();
//     }
//   }, [isEditMode, id, navigate, API_BASE_URL]);

//   // Update local state when sharedFormData changes (for create mode)
//   useEffect(() => {
//     if (!isEditMode && sharedFormData) {
//       setFormData({
//         name: sharedFormData.name || "",
//         batch: sharedFormData.batch || "",
//         mobile: sharedFormData.mobile || "",
//         email: sharedFormData.email || "",
//         profession: sharedFormData.profession || "",
//         officeAddress: sharedFormData.officeAddress || "",
//         permanentAddress: sharedFormData.permanentAddress || "",
//         professionalDetails: sharedFormData.professionalDetails || "",
//         bloodGroup: sharedFormData.bloodGroup || "",
//         imageUrl: sharedFormData.imageUrl || "",
//       });
//     }
//   }, [sharedFormData, isEditMode]);

//   // Fetch batch pricing
//   useEffect(() => {
//     const fetchBatchPricing = async () => {
//       try {
//         const data = await api.batchPricing.getAll();
//         const pricingMap = {};
//         data.forEach((bp) => {
//           pricingMap[bp.batchName] = {
//             participantFee: bp.participantFee,
//             adultFee: bp.adultFee,
//             above10Fee: bp.above10Fee,
//             below10Fee: bp.below10Fee,
//           };
//         });
//         setBatchPricing(pricingMap);
//       } catch (err) {
//         console.error("Error fetching batch pricing:", err);
//       }
//     };

//     fetchBatchPricing();
//   }, []);

//   // Search for existing directory by mobile
//   const handleSearchDirectory = async () => {
//     if (!searchMobile || searchMobile.length < 11) {
//       setSearchError("Please enter a valid 11-digit mobile number");
//       return;
//     }

//     try {
//       setSearchLoading(true);
//       setSearchError("");
//       setExistingDirectory(null);
//       setIsNewEntry(false);

//       const response = await api.directory.searchByMobile(searchMobile);

//       if (response && response.length > 0) {
//         // Found existing directory - SHOW VIEW ONLY
//         const directory = response[0];
//         setExistingDirectory(directory);

//         // Auto-fill the form with existing data (read-only)
//         setFormData({
//           name: directory.name || "",
//           batch: directory.batch || "",
//           mobile: directory.mobile || searchMobile,
//           email: directory.email || "",
//           profession: directory.profession || "",
//           officeAddress: directory.officeAddress || "",
//           permanentAddress: directory.permanentAddress || "",
//           professionalDetails: directory.professionalDetails || "",
//           bloodGroup: directory.bloodGroup || "",
//           imageUrl: directory.imageUrl || "",
//         });

//         setOriginalMobile(directory.mobile || searchMobile);

//         if (directory.imageUrl) {
//           setImagePreview(`${API_BASE_URL}${directory.imageUrl}`);
//         }

//         setShowForm(true);
//         setIsNewEntry(false);
//       } else {
//         // No existing directory found - ALLOW CREATE NEW
//         setFormData({
//           name: "",
//           batch: "",
//           mobile: searchMobile,
//           email: "",
//           profession: "",
//           officeAddress: "",
//           permanentAddress: "",
//           professionalDetails: "",
//           bloodGroup: "",
//           imageUrl: "",
//         });

//         setOriginalMobile(searchMobile);
//         setImagePreview(null);
//         setShowForm(true);
//         setIsNewEntry(true);
//       }
//     } catch (error) {
//       console.error("Error searching directory:", error);
//       setSearchError("Failed to search directory. Please try again.");
//     } finally {
//       setSearchLoading(false);
//     }
//   };

//   const handleResetSearch = () => {
//     setSearchMobile("");
//     setExistingDirectory(null);
//     setShowForm(false);
//     setIsNewEntry(false);
//     setFormData({
//       name: "",
//       batch: "",
//       mobile: "",
//       email: "",
//       profession: "",
//       officeAddress: "",
//       permanentAddress: "",
//       professionalDetails: "",
//       bloodGroup: "",
//       imageUrl: "",
//     });
//     setOriginalMobile("");
//     setImagePreview(null);
//     setSearchError("");
//   };

//   const handleInputChange = (field, value) => {
//     if (isNewEntry) {
//       // Prevent mobile field from being changed
//       if (field === "mobile") {
//         return; // Don't allow mobile to be changed
//       }

//       setFormData((prev) => ({ ...prev, [field]: value }));

//       if (!isEditMode && updateSharedFormData) {
//         updateSharedFormData(field, value);
//       }

//       if (errors[field]) {
//         setErrors((prev) => ({ ...prev, [field]: "" }));
//       }
//     }
//   };

//   // Ensure mobile field is always set to originalMobile
//   useEffect(() => {
//     if (originalMobile && isNewEntry) {
//       setFormData((prev) => ({
//         ...prev,
//         mobile: originalMobile,
//       }));
//     }
//   }, [originalMobile, isNewEntry]);

//   const handleHomeClick = () => {
//     navigate("/home");
//   };

//   const handleRegClick = () => {
//     // localStorage.setItem("directoryFormData", JSON.stringify(formData));
//     // navigate("/dir/register");
//     navigate(`/dir/register?mobile=${originalMobile || formData.mobile}`);
//   };

//   const validateForm = () => {
//     const newErrors = {};

//     // Name validation
//     if (!formData.name || !formData.name.trim()) {
//       newErrors.name = "Name is required";
//     }

//     // Mobile validation - always use originalMobile
//     const mobileToValidate = originalMobile || formData.mobile;
//     if (!mobileToValidate || !mobileToValidate.trim()) {
//       newErrors.mobile = "Mobile number is required";
//     } else if (!/^\d{11}$/.test(mobileToValidate)) {
//       newErrors.mobile = "Mobile must be 11 digits";
//     }

//     // Email validation - optional
//     if (
//       formData.email &&
//       formData.email.trim() &&
//       !/\S+@\S+\.\S+/.test(formData.email)
//     ) {
//       newErrors.email = "Please enter a valid email address";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleImageUpload = async (file) => {
//     if (!file || !isNewEntry) return;

//     const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
//     const maxSize = 10 * 1024 * 1024; // 10MB

//     if (!validTypes.includes(file.type)) {
//       alert("Please upload a valid image file (JPEG, PNG, GIF, WebP)");
//       return;
//     }

//     if (file.size > maxSize) {
//       alert("Image size should be less than 10MB");
//       return;
//     }

//     setUploadingImage(true);

//     try {
//       const formData = new FormData();
//       formData.append("file", file);

//       const response = await fetch(`${API_BASE_URL}/api/upload/image`, {
//         method: "POST",
//         body: formData,
//       });

//       if (!response.ok) throw new Error("Upload failed");

//       const data = await response.json();

//       handleInputChange("imageUrl", data.url);

//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setImagePreview(reader.result);
//       };
//       reader.readAsDataURL(file);
//     } catch (error) {
//       console.error("Image upload error:", error);
//       alert("Failed to upload image");
//     } finally {
//       setUploadingImage(false);
//     }
//   };

//   const handleRemoveImage = () => {
//     if (isNewEntry) {
//       handleInputChange("imageUrl", "");
//       setImagePreview(null);
//     }
//   };

//   const handleSubmit = async () => {
//     console.log("Form data before submission:", formData);
//     console.log("Original mobile:", originalMobile);

//     // Always use originalMobile for validation and submission
//     const mobileToUse = originalMobile || formData.mobile;

//     if (!validateForm()) {
//       console.log("Validation failed with errors:", errors);
//       return;
//     }

//     // Double-check required fields
//     if (!formData.name || !formData.name.trim()) {
//       alert("Please enter a name");
//       return;
//     }

//     if (!mobileToUse || !/^\d{11}$/.test(mobileToUse)) {
//       alert("Please ensure mobile number is 11 digits");
//       return;
//     }

//     setLoading(true);
//     try {
//       const payload = {
//         name: formData.name || "",
//         batch: formData.batch || "",
//         mobile: mobileToUse || "", // Use the guaranteed mobile value
//         email: formData.email || "",
//         profession: formData.profession || "",
//         officeAddress: formData.officeAddress || "",
//         permanentAddress: formData.permanentAddress || "",
//         professionalDetails: formData.professionalDetails || "",
//         bloodGroup: formData.bloodGroup || "",
//         imageUrl: formData.imageUrl || "",
//         userId: user?.id || null,
//       };

//       console.log("Saving payload:", payload);

//       await api.directory.create(payload);
//       alert("Directory information saved successfully ✅");

//       // Reset form
//       handleResetSearch();

//       setShowSuccess(true);
//       setTimeout(() => setShowSuccess(false), 3000);
//     } catch (error) {
//       console.error("Save error:", error);
//       alert("Failed to save directory");
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (isFetching) {
//     return (
//       <div className="form-container px-4 flex items-center justify-center">
//         <div className="text-center">
//           <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-4"></div>
//           <p>Loading directory data...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="form-container px-4">
//       <h2 className="form-title">
//         {isEditMode ? "Edit Directory Entry" : "Directory"}
//       </h2>
//       <h5
//         style={{ textAlign: "center", color: "#58db8c", marginBottom: "20PX" }}
//       >
//         KGC'97 Day 2026
//       </h5>

//       {showSuccess && (
//         <div className="success-message">🎉 Directory saved successfully!</div>
//       )}

//       {/* Mobile Search Section */}
//       {!isEditMode && !showForm && (
//         <div className="search-section mb-6">
//           <div className="text-center mb-4">
//             <p className="text-gray-300">Enter your 11 digits mobile number</p>
//           </div>
//           <div className="row justify-content-center">
//             <div className="col-12 col-md-8 col-lg-6">
//               <div className="input-group input-group-lg">
//                 <input
//                   type="text"
//                   className="form-control bg-light text-dark border-green-500"
//                   placeholder="01XXXXXXXX"
//                   value={searchMobile}
//                   onChange={(e) => {
//                     const value = e.target.value.replace(/\D/g, "");
//                     if (value.length <= 11) {
//                       setSearchMobile(value);
//                       setSearchError("");
//                     }
//                   }}
//                   maxLength="11"
//                   style={{ height: "50px" }}
//                 />
//                 <button
//                   className="btn btn-search d-flex align-items-center justify-content-center"
//                   onClick={handleSearchDirectory}
//                   disabled={searchLoading || !searchMobile.trim()}
//                   style={{
//                     height: "50px",
//                     width: "60px",
//                     padding: "0 15px",
//                   }}
//                   title="Search"
//                 >
//                   {searchLoading ? (
//                     <span className="spinner-border spinner-border-sm" />
//                   ) : (
//                     <ArrowRight size={24} />
//                   )}
//                 </button>
//               </div>
//               {searchError && (
//                 <div className="text-danger small mt-2 text-center">
//                   {searchError}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Show form if in edit mode OR after search */}
//       {(isEditMode || showForm) && (
//         <form onSubmit={(e) => e.preventDefault()}>
//           {/* Existing Directory Notification */}
//           {existingDirectory && !isNewEntry && (
//             <div className="alert alert-info mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>✓ Directory Entry Found</strong>
//                   <div className="small">
//                     This directory entry already exists. You can view the
//                     details below.
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* New Entry Notification */}
//           {isNewEntry && (
//             <div className="alert alert-warning mb-4">
//               <div className="d-flex align-items-center justify-content-between">
//                 <div>
//                   <strong>➕ Create New Directory Entry</strong>
//                   <div className="small">
//                     No existing directory found for mobile: {originalMobile}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Mobile Field - Fixed display that won't change */}
//           <div className="form-group">
//             <label className="form-label">মোবাইল নম্বর*</label>
//             <div className="mobile-display">
//               <div
//                 style={{
//                   padding: "12px",
//                   backgroundColor: "#e9ecef",
//                   color: "#495057",
//                   borderRadius: "5px",
//                   border: "1px solid #dee2e6",
//                   fontSize: "16px",
//                   fontWeight: "bold",
//                 }}
//               >
//                 {originalMobile || formData.mobile || "No mobile number"}
//               </div>
//               {/* Hidden input to store the mobile value for form submission */}
//               <input
//                 type="hidden"
//                 name="mobile"
//                 value={originalMobile || formData.mobile || ""}
//               />
//             </div>
//             {errors.mobile && (
//               <div className="error-message">{errors.mobile}</div>
//             )}
//           </div>

//           {/* Name */}
//           <div className="form-group">
//             <label className="form-label">নাম *</label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your full name"
//               value={formData.name}
//               onChange={(e) => handleInputChange("name", e.target.value)}
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               style={
//                 !isNewEntry
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.name && <div className="error-message">{errors.name}</div>}
//           </div>

//           {/* Email */}
//           <div className="form-group">
//             <label className="form-label">ইমেইল </label>
//             <input
//               type="email"
//               className="form-input"
//               placeholder="your.email@example.com"
//               value={formData.email}
//               onChange={(e) => handleInputChange("email", e.target.value)}
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               style={
//                 !isNewEntry
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.email && (
//               <div className="error-message">{errors.email}</div>
//             )}
//           </div>

//           {/* Batch */}
//           <div style={{ width: "100%", position: "relative" }}>
//             <label className="form-label">বিভাগ </label>
//             <select
//               value={formData.batch}
//               onChange={(e) => handleInputChange("batch", e.target.value)}
//               disabled={loading || uploadingImage || !isNewEntry}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background: !isNewEntry ? "#e9ecef" : "rgba(255,255,255,0.1)",
//                 color: !isNewEntry ? "#495057" : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   !isNewEntry ? "#495057" : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your group
//               </option>
//               {Object.keys(batchPricing).map((batch) => (
//                 <option
//                   key={batch}
//                   value={batch}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {batch}
//                 </option>
//               ))}
//             </select>
//             {errors.batch && (
//               <div className="error-message">{errors.batch}</div>
//             )}
//           </div>

//           {/* Profession */}
//           <div className="form-group">
//             <label className="form-label">পেশা </label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your profession"
//               value={formData.profession}
//               onChange={(e) => handleInputChange("profession", e.target.value)}
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               style={
//                 !isNewEntry
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.profession && (
//               <div className="error-message">{errors.profession}</div>
//             )}
//           </div>

//           {/* professionalDetails */}
//           <div className="form-group">
//             <label className="form-label">পদবি, অফিসের নাম </label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter designation & office details"
//               value={formData.professionalDetails}
//               onChange={(e) =>
//                 handleInputChange("professionalDetails", e.target.value)
//               }
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               style={
//                 !isNewEntry
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.professionalDetails && (
//               <div className="error-message">{errors.professionalDetails}</div>
//             )}
//           </div>

//           {/* Office Address */}
//           <div className="form-group">
//             <label className="form-label">বর্তমান ঠিকনা</label>
//             <textarea
//               className="form-input"
//               placeholder="Enter your present address"
//               value={formData.officeAddress}
//               onChange={(e) =>
//                 handleInputChange("officeAddress", e.target.value)
//               }
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               rows={2}
//               style={
//                 !isNewEntry
//                   ? {
//                       backgroundColor: "#e9ecef",
//                       color: "#495057",
//                       resize: "none",
//                     }
//                   : { resize: "vertical" }
//               }
//             />
//             {errors.officeAddress && (
//               <div className="error-message">{errors.officeAddress}</div>
//             )}
//           </div>
//           {/* <div className="form-group">
//             <label className="form-label">কর্মস্থলের ঠিকনা </label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your office address"
//               value={formData.officeAddress}
//               onChange={(e) =>
//                 handleInputChange("officeAddress", e.target.value)
//               }
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               style={
//                 !isNewEntry
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.officeAddress && (
//               <div className="error-message">{errors.officeAddress}</div>
//             )}
//           </div> */}

//           {/* Permanent Address */}
//           <div className="form-group">
//             <label className="form-label">স্থায়ী ঠিকনা</label>
//             <textarea
//               className="form-input"
//               placeholder="Enter your permanent address"
//               value={formData.permanentAddress}
//               onChange={(e) =>
//                 handleInputChange("permanentAddress", e.target.value)
//               }
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               rows={2} // Number of visible lines
//               style={
//                 !isNewEntry
//                   ? {
//                       backgroundColor: "#e9ecef",
//                       color: "#495057",
//                       resize: "none", // Optional: disable resizing
//                     }
//                   : { resize: "vertical" } // Allow vertical resizing only
//               }
//             />
//             {errors.permanentAddress && (
//               <div className="error-message">{errors.permanentAddress}</div>
//             )}
//           </div>

//           {/* <div className="form-group">
//             <label className="form-label">স্থায়ী ঠিকনা </label>
//             <input
//               type="text"
//               className="form-input"
//               placeholder="Enter your permanent address"
//               value={formData.permanentAddress}
//               onChange={(e) =>
//                 handleInputChange("permanentAddress", e.target.value)
//               }
//               disabled={loading || uploadingImage || !isNewEntry}
//               readOnly={!isNewEntry}
//               style={
//                 !isNewEntry
//                   ? { backgroundColor: "#e9ecef", color: "#495057" }
//                   : {}
//               }
//             />
//             {errors.permanentAddress && (
//               <div className="error-message">{errors.permanentAddress}</div>
//             )}
//           </div> */}

//           {/* Blood Group */}
//           <div className="form-group">
//             <label className="form-label">রক্তের গ্রুপ </label>
//             <select
//               value={formData.bloodGroup}
//               onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
//               disabled={loading || uploadingImage || !isNewEntry}
//               style={{
//                 width: "100%",
//                 padding: "8px 15px",
//                 borderRadius: "0px",
//                 border: "1px solid rgba(255, 255, 255, 0.2)",
//                 background: !isNewEntry ? "#e9ecef" : "rgba(255,255,255,0.1)",
//                 color: !isNewEntry ? "#495057" : "white",
//                 fontSize: "1rem",
//                 appearance: "none",
//                 WebkitAppearance: "none",
//                 MozAppearance: "none",
//                 backdropFilter: "blur(8px)",
//                 transition: "all 0.3s ease",
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                   !isNewEntry ? "#495057" : "white"
//                 }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//                 backgroundRepeat: "no-repeat",
//                 backgroundPosition: "right 12px center",
//                 backgroundSize: "1rem",
//                 marginBottom: "15px",
//               }}
//             >
//               <option
//                 value=""
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 Select your blood group
//               </option>
//               {bloodGroups.map((bloodGroup) => (
//                 <option
//                   key={bloodGroup}
//                   value={bloodGroup}
//                   style={{ backgroundColor: "#cffae0", color: "black" }}
//                 >
//                   {bloodGroup}
//                 </option>
//               ))}
//             </select>
//             {errors.bloodGroup && (
//               <div className="error-message">{errors.bloodGroup}</div>
//             )}
//           </div>

//           {/* Image Upload (only for new entries) */}
//           {isNewEntry && (
//             <div className="form-group">
//               <label className="form-label">ছবি আপলোড</label>

//               {imagePreview || formData.imageUrl ? (
//                 <div className="relative mb-3">
//                   <div className="relative w-32 h-32 mx-auto">
//                     <img
//                       src={
//                         imagePreview || `${API_BASE_URL}${formData.imageUrl}`
//                       }
//                       alt="Preview"
//                       className="w-full h-full object-cover rounded-lg border-2 border-white/20"
//                     />
//                     <button
//                       type="button"
//                       onClick={handleRemoveImage}
//                       className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
//                       disabled={uploadingImage}
//                     >
//                       <X size={16} />
//                     </button>
//                   </div>
//                 </div>
//               ) : (
//                 <div
//                   className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-white/40 transition"
//                   onClick={() =>
//                     document.getElementById("image-upload").click()
//                   }
//                 >
//                   <Upload className="mx-auto mb-2 text-white" size={24} />
//                   <p className="text-sm text-white">ছবি আপলোড করুন</p>
//                   <p className="text-xs text-white/60 mt-1">
//                     JPEG, PNG, GIF, WebP (Max 10MB)
//                   </p>
//                 </div>
//               )}

//               <input
//                 id="image-upload"
//                 type="file"
//                 accept="image/*"
//                 className="hidden"
//                 onChange={(e) => {
//                   if (e.target.files[0]) {
//                     handleImageUpload(e.target.files[0]);
//                   }
//                 }}
//                 disabled={uploadingImage || loading}
//               />

//               {uploadingImage && (
//                 <div className="text-center mt-2">
//                   <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
//                   <p className="text-sm mt-1">আপলোড হচ্ছে...</p>
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Show existing image (read-only) */}
//           {!isNewEntry && formData.imageUrl && (
//             <div className="form-group">
//               <label className="form-label">ছবি</label>
//               <div className="relative mb-3">
//                 <div className="relative w-32 h-32 mx-auto">
//                   <img
//                     src={`${API_BASE_URL}${formData.imageUrl}`}
//                     alt="Directory"
//                     className="w-full h-full object-cover rounded-lg border-2 border-white/20"
//                   />
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* Buttons */}
//           <div className="button-group flex gap-4">
//             {isNewEntry && (
//               <button
//                 type="button"
//                 className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//                 style={{ backgroundColor: "#1cbd2f" }}
//                 onClick={handleSubmit}
//                 disabled={loading || uploadingImage}
//               >
//                 {loading ? "Saving..." : "Submit"}
//               </button>
//             )}

//             {/* {!isEditMode && (
//               <button
//                 type="button"
//                 className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//                 onClick={handleResetSearch}
//                 disabled={loading || uploadingImage}
//               >
//                 <ArrowRight size={18} />
//                 New Search
//               </button>
//             )} */}

//             <button
//               type="button"
//               className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//               onClick={handleHomeClick}
//               disabled={loading || uploadingImage}
//             >
//               <Home size={18} />
//               Go To Home
//             </button>
//             {/* <button
//               type="button"
//               className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//               onClick={handleRegClick}
//               disabled={loading || uploadingImage}
//             >
//               Go To Registration
//               <ArrowBigRight size={18} />
//             </button> */}
//           </div>
//         </form>
//       )}

//       {/* Show only home button if not showing form and not in edit mode */}
//       {!isEditMode && !showForm && (
//         <div className="flex justify-center text-center mt-4">
//           <button
//             type="button"
//             className="flex justify-center items-center gap-2 px-6 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={handleHomeClick}
//           >
//             <Home size={18} />
//             Go To Home
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default DirectoryPage;
